import { type ApiHandlerContract, ApiService } from '../../../types/api';
import type { CompanyLookup } from '../../companies/types/api';
import type { SectionLookup, SectionRequest, SectionResponse } from '../types/api';
import { SectionApi, type SectionApiContract } from './section.api';

/**
 * A service contract to interact with sections via the SparkLayer API.
 */
export interface SectionsApiContract {
  get(leafOnly?: boolean): Promise<SectionResponse[]>;
  create(payload: SectionRequest): Promise<SectionResponse>;
  section(lookup: SectionLookup): SectionApiContract;
}

/**
 * A service to interact with sections via the SparkLayer API.
 */
export class SectionsApi extends ApiService implements SectionsApiContract {
  constructor(
    api: ApiHandlerContract,
    private companyLookup: CompanyLookup,
  ) {
    super(api);
  }

  get(leafOnly?: boolean): Promise<SectionResponse[]> {
    const params = new URLSearchParams();
    if (leafOnly !== undefined) {
      params.set('leafOnly', String(leafOnly));
    }

    return this.api.proxy({
      method: 'GET',
      path: `/v2/companies/{lookupBy}/{identifier}/sections?${params}`,
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  create(payload: SectionRequest): Promise<SectionResponse> {
    return this.api.proxy({
      method: 'POST',
      path: '/v2/companies/{lookupBy}/{identifier}/sections',
      payload,
      params: {
        lookupBy: this.companyLookup.by,
        identifier: this.companyLookup.identifier,
      },
    });
  }

  section(lookup: SectionLookup): SectionApiContract {
    return new SectionApi(this.api, this.companyLookup, lookup);
  }
}
